import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import useSWR from "swr";
import { LayoutDashboard, Zap, ShoppingBag, Layers, Barcode, PieChart, AlertTriangle, TrendingDown, Clock, ShieldAlert, FileText, GitCompare, UploadCloud, Activity, Settings, Search, Moon, Sun, LogOut, Cpu, Target, Truck, BarChart3, Info, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetcher } from "@/lib/api";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "./GlobalSearch";
import { AboutDialog } from "./AboutDialog";
import { AlertsBell } from "./AlertsBell";

const NAV = [
  { cat: "Command Centre", items: [
    { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true, badge: "Live" },
    { to: "/actions", label: "Action Centre", icon: Zap, badgeKey: "pending" } ] },
  { cat: "Merchandise", items: [
    { to: "/merchandise?tab=styles", label: "Styles", icon: ShoppingBag },
    { to: "/merchandise?tab=options", label: "Options · Option 360", icon: Layers },
    { to: "/merchandise?tab=skus", label: "SKUs · SKU 360", icon: Barcode } ] },
  { cat: "Inventory Intelligence", items: [
    { to: "/inventory/overview", label: "Overview & Position", icon: PieChart },
    { to: "/inventory/stockouts", label: "Stock-outs", icon: AlertTriangle },
    { to: "/inventory/overstock", label: "Overstock", icon: TrendingDown },
    { to: "/inventory/ageing", label: "Ageing", icon: Clock },
    { to: "/inventory/exceptions", label: "Exceptions", icon: ShieldAlert },
    { to: "/inventory/ledger", label: "Stock Movement Ledger", icon: FileText },
    { to: "/inventory/reconciliation", label: "Reconciliation", icon: GitCompare } ] },
  { cat: "Engines (rule-based)", items: [
    { to: "/engines", label: "Engines & Rules", icon: Cpu } ] },
  { cat: "Planning & Allocation", items: [
    { to: "/planning", label: "Planning", icon: Target },
    { to: "/allocation", label: "Allocation", icon: Truck },
    { to: "/performance", label: "Performance", icon: BarChart3 },
    { to: "/reports", label: "Reports & Analytics", icon: FileText } ] },
  { cat: "Data", items: [
    { to: "/data/upload", label: "Upload Centre", icon: UploadCloud },
    { to: "/data/health", label: "Data Health", icon: Activity } ] },
  { cat: "Administration", items: [
    { to: "/admin", label: "Admin & Audit Trail", icon: Settings } ] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useState(() => localStorage.getItem("kazo_theme") === "dark");
  const [searchOpen, setSearchOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { data: summary } = useSWR("/dashboard/summary", fetcher, { refreshInterval: 60000 });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("kazo_theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const pending = summary?.actions?.pending;

  return (
    <div className="min-h-screen flex bg-background">
      <aside data-testid="sidebar" className={cn("fixed inset-y-0 left-0 z-30 flex flex-col bg-slate-900 text-slate-100 border-r border-slate-800 transition-[width] duration-200", collapsed ? "w-16" : "w-[250px]")}>
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-800">
          <div className="h-8 w-8 rounded-md bg-[#D4AF37] text-slate-900 font-heading font-extrabold flex items-center justify-center text-sm shrink-0">K</div>
          {!collapsed && <div className="leading-tight"><div className="font-heading font-extrabold tracking-[0.2em] text-sm">KAZO</div><div className="text-[10px] text-slate-400 tracking-wide">Merchandise Intelligence OS</div></div>}
        </div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin px-2.5 py-3 space-y-4">
          {NAV.map((g) => (
            <div key={g.cat}>
              {!collapsed && <div className="eyebrow !text-slate-500 px-3 mb-1.5">{g.cat}</div>}
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) => cn("nav-item", (isActive || (it.to.includes("?") && location.pathname + location.search === it.to)) && !(it.to.includes("?") && location.pathname + location.search !== it.to) && "active")} title={it.label}>
                    <it.icon size={16} className="shrink-0" />
                    {!collapsed && <span className="flex-1 truncate">{it.label}</span>}
                    {!collapsed && it.badge && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">{it.badge}</span>}
                    {!collapsed && it.badgeKey && pending != null && <span data-testid="nav-pending-badge" className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">{pending}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <button data-testid="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)} className="nav-item w-full mb-1"><ChevronLeft size={16} className={cn("transition-transform", collapsed && "rotate-180")} />{!collapsed && <span>Collapse</span>}</button>
          {!collapsed && <div data-testid="sidebar-powered-by" className="text-[10px] text-slate-500 px-3">Powered by <span className="text-slate-300 font-semibold">Fundle</span></div>}
        </div>
      </aside>

      <div className={cn("flex-1 flex flex-col min-w-0 transition-[margin] duration-200", collapsed ? "ml-16" : "ml-[250px]")}>
        <header data-testid="app-header" className="sticky top-0 z-20 h-14 flex items-center gap-3 px-4 lg:px-6 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
          <button data-testid="global-search-btn" onClick={() => setSearchOpen(true)} className="flex items-center gap-2 h-9 w-full max-w-md px-3 rounded-md border border-slate-200 dark:border-slate-700 text-sm text-slate-500 hover:border-slate-400 transition-colors">
            <Search size={15} /> <span className="flex-1 text-left">Search SKU, option, style, barcode, store…</span><kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded border">Ctrl K</kbd>
          </button>
          <div className="flex-1" />
          {summary && <div data-testid="header-asof" className="hidden md:block text-xs text-slate-500">As of <span className="font-mono">{summary.as_of}</span></div>}
          <AlertsBell />
          <button data-testid="about-btn" onClick={() => setAboutOpen(true)} className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800" title="About"><Info size={16} /></button>
          <button data-testid="theme-toggle" onClick={() => setDark(!dark)} className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800">{dark ? <Sun size={16} /> : <Moon size={16} />}</button>
          <div className="flex items-center gap-2.5 pl-3 border-l border-slate-200 dark:border-slate-800">
            <div className="h-8 w-8 rounded-full bg-slate-900 dark:bg-slate-700 text-white flex items-center justify-center text-xs font-semibold">{user?.name?.[0]}</div>
            <div className="hidden sm:block leading-tight"><div data-testid="header-user-name" className="text-sm font-medium">{user?.name}</div><div data-testid="header-user-role" className="text-[11px] text-slate-500">{user?.role_label}</div></div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800" title="Sign out"><LogOut size={16} /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
        <footer data-testid="dashboard-footer" className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 flex items-center justify-between">
          <span>KAZO Merchandise Intelligence OS · every metric computed from the normalized ledger</span>
          <span>Powered by <span className="font-semibold text-slate-700 dark:text-slate-300">Fundle</span></span>
        </footer>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}
